/**
 * ShareProjectDialog - Share a project with another user by email
 *
 * Features:
 * - Email input with permission selector (VIEW / CLONE)
 * - List existing shares with revoke action
 * - Toast notifications on success/error
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  UserPlus,
  Loader2,
  X,
} from 'lucide-react';
import { toast } from 'sonner';
import { projectShareAPI, type ProjectShareInfo } from '@/services/api';

interface ShareProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  projectTitle: string;
}

export function ShareProjectDialog({
  open,
  onOpenChange,
  conversationId,
  projectTitle,
}: ShareProjectDialogProps) {
  const [email, setEmail] = useState('');
  const [permission, setPermission] = useState<'VIEW' | 'CLONE'>('CLONE');
  const [isSharing, setIsSharing] = useState(false);
  const [shares, setShares] = useState<ProjectShareInfo[]>([]);
  const [loadingShares, setLoadingShares] = useState(false);

  const loadShares = useCallback(async () => {
    if (!conversationId) return;
    try {
      setLoadingShares(true);
      const result = await projectShareAPI.listShares(conversationId);
      setShares(result.shares);
    } catch {
      // Silently fail — shares list is supplementary
    } finally {
      setLoadingShares(false);
    }
  }, [conversationId]);

  useEffect(() => {
    if (open) {
      loadShares();
      setEmail('');
    }
  }, [open, loadShares]);

  const handleShare = async () => {
    if (!email.trim()) return;

    try {
      setIsSharing(true);
      const result = await projectShareAPI.share(conversationId, email.trim(), permission);
      toast.success(`Shared with ${result.share.sharedWith.email}`);
      setEmail('');
      loadShares();
    } catch (error: any) {
      const msg = error?.response?.data?.error || error.message || 'Failed to share';
      toast.error(msg);
    } finally {
      setIsSharing(false);
    }
  };

  const handleRevoke = async (share: ProjectShareInfo) => {
    try {
      await projectShareAPI.revoke(conversationId, share.id);
      toast.success(`Removed ${share.sharedWith.email}`);
      loadShares();
    } catch {
      toast.error('Failed to revoke share');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Share Project
          </DialogTitle>
          <DialogDescription className="truncate">
            {projectTitle}
          </DialogDescription>
        </DialogHeader>

        {/* Share form */}
        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Enter email address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleShare()}
              className="flex-1"
            />
            <Select value={permission} onValueChange={(v) => setPermission(v as 'VIEW' | 'CLONE')}>
              <SelectTrigger className="w-[100px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="CLONE">Clone</SelectItem>
                <SelectItem value="VIEW">View</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={handleShare}
            disabled={!email.trim() || isSharing}
            className="w-full"
          >
            {isSharing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4 mr-2" />
            )}
            Share
          </Button>
        </div>

        {/* Existing shares */}
        {shares.length > 0 && (
          <div className="space-y-2 pt-4 border-t">
            <p className="text-sm font-medium text-muted-foreground">
              Shared with {shares.length} {shares.length === 1 ? 'person' : 'people'}
            </p>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {shares.map((share) => (
                <div
                  key={share.id}
                  className="flex items-center justify-between gap-2 p-2 rounded-lg bg-muted/50"
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {share.sharedWith.displayName || share.sharedWith.email}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {share.sharedWith.email}
                    </p>
                  </div>
                  <Badge variant="secondary" className="text-xs flex-shrink-0">
                    {share.permission === 'CLONE' ? 'Can clone' : 'Can view'}
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0 flex-shrink-0 text-muted-foreground hover:text-red-500"
                    onClick={() => handleRevoke(share)}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        {loadingShares && shares.length === 0 && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
